"use client";

import * as React from "react";
import { useState, useCallback } from "react";
import { cn } from "@repo/utils/cn";
import { Link } from "./Link";
import Image from "next/image";
import { AnimatePresence, motion } from "motion/react";
import { NavbarProps } from "./types";
import { useClickOutside } from "./hooks/useClickOutside";
import { SearchIcon } from "./icons/SearchIcon";
import { GlobeIcon } from "./icons/GlobeIcon";
import { ChevronDownIcon } from "./icons/ChevronDownIcon";
import { ChevronRightIcon } from "./icons/ChevronRightIcon";
import { Button } from "./Button";

export interface NavbarDesktopProps extends NavbarProps {}

export function NavbarDesktop({
  logo,
  logoHref = "/",
  noFollow,
  items,
  language = "EN",
  onSearchClick,
  onLanguageClick,
  onSignInClick,
  onLogoClick,
  onTopLevelLinkClick,
  onCategoryLinkClick,
  onSubCategoryLinkClick,
  signInText = "Sign in",
  signInHref,
  signInNoFollow,
  signInItems,
  activeTopLevelItemId,
  activeSubItemId,
  pathname,
  className,
}: NavbarDesktopProps) {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeSubgroup, setActiveSubgroup] = useState<string | null>(null);

  const handleClickOutside = useCallback(() => {
    setActiveDropdown(null);
    setActiveSubgroup(null);
  }, []);

  const dropdownRef = useClickOutside<HTMLDivElement>(
    handleClickOutside,
    activeDropdown !== null,
  );

  const handleItemClick = (item: any) => {
    if (item.hasDropdown && item.columns) {
      if (activeDropdown !== item.id) {
        onTopLevelLinkClick?.(item.title, item.href || "#");
      }
      setActiveDropdown(activeDropdown === item.id ? null : item.id);
      setActiveSubgroup(null);
    }
  };

  return (
    <>
      <nav
        className={cn(
          "uipm:bg-[var(--surface-surface-primary)] uipm:fixed uipm:left-0 uipm:right-0 uipm:z-50",
          className,
        )}
        style={{ top: "var(--notification-banner-height, 0px)" }}
        ref={dropdownRef}
        data-navbar
      >
        <div className="uipm:max-w-[1440px] uipm:mx-auto uipm:px-[var(--container-margin)]">
          <div className="uipm:flex uipm:items-center uipm:w-full uipm:h-[90px] uipm:py-4">
            <div className="uipm:flex uipm:items-center uipm:flex-1">
              {logo && (
                <Link
                  href={logoHref}
                  noFollow={noFollow}
                  onClick={onLogoClick}
                  aria-label={logo.alt || "Home"}
                  className="uipm:shrink-0 uipm:mr-10 uipm:cursor-pointer"
                >
                  <Image
                    src={logo.src}
                    alt={logo.alt}
                    width={logo.width}
                    height={logo.height}
                    className="uipm:h-[32px] uipm:w-auto"
                  />
                </Link>
              )}

              <div className="uipm:flex uipm:items-center uipm:gap-[var(--container-gutter)]">
                {items.map((item) => {
                  const isActive = activeTopLevelItemId === item.id;
                  return (
                    <div key={item.id} className="uipm:relative">
                      {item.hasDropdown ? (
                        <button
                          onClick={() => handleItemClick(item)}
                          data-dropdown-id={item.id}
                          className={cn(
                            "uipm:flex uipm:items-center uipm:gap-[var(--padding-padding-xs)] uipm:text-(length:--typography-sizes-body-medium) uipm:font-medium uipm:leading-[var(--typography-line-heights-body-medium)] hover:uipm:text-[var(--typography-colors-text-accent)] uipm:transition-colors uipm:cursor-pointer",
                            activeDropdown === item.id || isActive
                              ? "uipm:text-[var(--color-color-accent)]"
                              : "uipm:text-[var(--typography-colors-text-faded-dark)]",
                          )}
                        >
                          {item.title}
                          <ChevronDownIcon
                            className="uipm:transition-transform"
                            color="var(--typography-colors-text-accent)"
                            style={{
                              transform:
                                activeDropdown === item.id
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                            }}
                          />
                        </button>
                      ) : (
                        <Link
                          href={item.href || "#"}
                          noFollow={item.noFollow}
                          className={cn(
                            "uipm:text-(length:--typography-sizes-body-medium) uipm:font-medium uipm:leading-[var(--typography-line-heights-body-medium)] hover:uipm:text-[var(--typography-colors-text-accent)] uipm:transition-colors uipm:cursor-pointer",
                            isActive
                              ? "uipm:text-[var(--color-color-accent)]"
                              : "uipm:text-[var(--typography-colors-text-faded-dark)]",
                          )}
                        >
                          {item.title}
                        </Link>
                      )}

                      <AnimatePresence>
                        {activeDropdown === item.id && item.columns && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="uipm:absolute uipm:top-[59px] uipm:left-0 uipm:bg-[var(--surface-surface-primary)] uipm:rounded-b-[20px] uipm:shadow-[0px_8px_32px_0px_rgba(31,47,70,0.08)] uipm:z-50 uipm:flex uipm:p-[var(--padding-padding-default)] uipm:gap-[var(--container-gutter)]"
                          >
                            {item.columns.map((column, columnIndex) => {
                              const parentTitle = item.title;
                              return (
                                <div
                                  key={column.id}
                                  className="uipm:w-[275px] uipm:flex uipm:flex-col uipm:gap-[var(--padding-padding-sm-plus)]"
                                >
                                  {column.title && (
                                    <h3 className="uipm:text-(length:--typography-sizes-body-large) uipm:font-bold uipm:text-[var(--typography-colors-text-accent)] uipm:leading-[var(--typography-line-heights-body-large)]">
                                      {column.title}
                                    </h3>
                                  )}
                                  <div className="uipm:space-y-2">
                                    {column.items.map((subItem) => {
                                      const isSubItemActive =
                                        activeSubItemId === subItem.id;
                                      return (
                                        <div
                                          key={subItem.id}
                                          onMouseEnter={() =>
                                            subItem.hasSubgroup &&
                                            setActiveSubgroup(subItem.id)
                                          }
                                        >
                                          {subItem.hasSubgroup ? (
                                            <Link
                                              href={subItem.href}
                                              noFollow={subItem.noFollow}
                                              className="uipm:cursor-pointer"
                                            >
                                              <div
                                                className={cn(
                                                  "uipm:flex uipm:items-center uipm:gap-[var(--container-margin)] uipm:p-[var(--container-gutter)] uipm:hover:bg-[var(--surface-surface-secondary)] uipm:rounded uipm:transition-colors",
                                                  isSubItemActive &&
                                                    "uipm:bg-[var(--surface-surface-secondary)]",
                                                )}
                                              >
                                                {subItem.icon && (
                                                  <div className="uipm:w-[30px] uipm:h-[30px] uipm:shrink-0">
                                                    <Image
                                                      src={subItem.icon.src}
                                                      alt={subItem.icon.alt}
                                                      width={30}
                                                      height={30}
                                                    />
                                                  </div>
                                                )}
                                                <div className="uipm:flex uipm:items-center uipm:justify-between uipm:flex-1">
                                                  <div
                                                    className={cn(
                                                      "uipm:text-(length:--typography-sizes-body-medium) uipm:font-bold uipm:leading-[var(--typography-line-heights-body-medium)]",
                                                      isSubItemActive
                                                        ? "uipm:text-[var(--color-color-accent)]"
                                                        : "uipm:text-[var(--typography-colors-text-faded-dark)]",
                                                    )}
                                                  >
                                                    {subItem.title}
                                                  </div>
                                                  <ChevronRightIcon
                                                    className="uipm:text-[var(--typography-colors-text-faded-dark)]"
                                                    color="var(--typography-colors-text-accent)"
                                                  />
                                                </div>
                                              </div>
                                            </Link>
                                          ) : (
                                            <Link
                                              href={subItem.href}
                                              noFollow={subItem.noFollow}
                                              onClick={() => {
                                                if (column.title) {
                                                  onCategoryLinkClick?.(
                                                    subItem.title,
                                                    subItem.href,
                                                    column.title,
                                                  );
                                                } else {
                                                  onSubCategoryLinkClick?.(
                                                    subItem.title,
                                                    subItem.href,
                                                    parentTitle,
                                                  );
                                                }
                                              }}
                                              className={cn(
                                                "uipm:flex uipm:items-center uipm:gap-[var(--container-margin)] uipm:py-2 uipm:hover:text-[var(--color-color-accent)] uipm:rounded uipm:transition-colors uipm:cursor-pointer",
                                                isSubItemActive
                                                  ? "uipm:text-[var(--color-color-accent)]"
                                                  : "uipm:text-[var(--typography-colors-text-faded-dark)]",
                                              )}
                                            >
                                              {subItem.icon && (
                                                <div className="uipm:w-[30px] uipm:h-[30px] uipm:shrink-0">
                                                  <Image
                                                    src={subItem.icon.src}
                                                    alt={subItem.icon.alt}
                                                    width={30}
                                                    height={30}
                                                  />
                                                </div>
                                              )}
                                              <div className="uipm:text-(length:--typography-sizes-body-medium) uipm:font-medium  uipm:leading-[var(--typography-line-heights-body-medium)]">
                                                {subItem.title}
                                              </div>
                                            </Link>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}

                            <AnimatePresence>
                              {activeSubgroup &&
                                item.columns
                                  .flatMap((col) => col.items)
                                  .find(
                                    (subItem) =>
                                      subItem.id === activeSubgroup &&
                                      subItem.hasSubgroup &&
                                      subItem.subgroupItems,
                                  ) && (
                                  <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ duration: 0.2 }}
                                    className="uipm:bg-[var(--surface-surface-secondary)] uipm:p-[24px] uipm:w-[275px] uipm:rounded-tr-[8px] uipm:rounded-br-[8px] uipm:self-stretch uipm:flex uipm:flex-col uipm:gap-4"
                                  >
                                    {(() => {
                                      const activeSubItem = item.columns
                                        .flatMap((col) => col.items)
                                        .find(
                                          (subItem) =>
                                            subItem.id === activeSubgroup,
                                        );
                                      const title =
                                        activeSubItem?.subgroupTitle ||
                                        activeSubItem?.title ||
                                        "";
                                      const link = activeSubItem?.subgroupLink;

                                      return link ? (
                                        <Link
                                          href={link}
                                          noFollow={activeSubItem?.noFollow}
                                          className="uipm:text-(length:--typography-sizes-body-medium) uipm:font-bold uipm:text-[var(--typography-colors-text-accent)] uipm:mb-4 uipm:block uipm:hover:underline"
                                        >
                                          {title}
                                        </Link>
                                      ) : (
                                        <h4 className="uipm:text-(length--typography-sizes-body-medium) uipm:font-bold uipm:text-[var(--typography-colors-text-accent)] uipm:mb-4">
                                          {title}
                                        </h4>
                                      );
                                    })()}
                                    <div className="uipm:space-y-[var(--container-gutter)]">
                                      {item.columns
                                        .flatMap((col) => col.items)
                                        .find(
                                          (subItem) =>
                                            subItem.id === activeSubgroup,
                                        )
                                        ?.subgroupItems?.map((subgroupItem) => (
                                          <Link
                                            key={subgroupItem.id}
                                            href={subgroupItem.href}
                                            noFollow={subgroupItem.noFollow}
                                            onClick={() =>
                                              onSubCategoryLinkClick?.(
                                                subgroupItem.title,
                                                subgroupItem.href,
                                                item.title,
                                              )
                                            }
                                            className="uipm:block uipm:p-[var(--container-gutter)] uipm:text-(length:--typography-sizes-body-medium) uipm:text-[var(--typography-colors-text-faded-dark)] uipm:leading-[var(--typography-line-heights-body-medium)] hover:uipm:bg-white uipm:rounded uipm:transition-colors uipm:cursor-pointer"
                                          >
                                            {subgroupItem.title}
                                          </Link>
                                        ))}
                                    </div>
                                  </motion.div>
                                )}
                            </AnimatePresence>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="uipm:flex uipm:items-center uipm:gap-10">
              {/*<button*/}
              {/*  onClick={onSearchClick}*/}
              {/*  className="uipm:w-6 uipm:h-6 uipm:hover:opacity-70 uipm:transition-opacity uipm:cursor-pointer"*/}
              {/*  aria-label="Search"*/}
              {/*>*/}
              {/*  <SearchIcon color="var(--typography-colors-text-accent)" />*/}
              {/*</button>*/}

              {/* <button
              onClick={onLanguageClick}
              className="uipm:flex uipm:items-center uipm:gap-[var(--padding-padding-xs)] uipm:text-(length:--typography-sizes-body-medium) uipm:font-medium uipm:text-[var(--typography-colors-text-faded-dark)] uipm:leading-[var(--typography-line-heights-body-medium)] uipm:hover:text-[var(--typography-colors-text-accent)] uipm:transition-colors uipm:cursor-pointer"
            >
              <GlobeIcon
                className="uipm:shrink-0"
                color="var(--typography-colors-text-accent)"
              />
              {language}
            </button> */}

              {signInItems && signInItems.length > 0 ? (
                <div className="uipm:flex uipm:items-center uipm:gap-4">
                  {signInItems.map((item, index) => {
                    if (item.variant === "button") {
                      return (
                        <Button
                          key={index}
                          href={item.href}
                          noFollow={item.noFollow}
                          target={item.target}
                          navatticUrl={item.navatticUrl}
                          navatticTitle={item.navatticTitle}
                          navatticMobileUrl={item.navatticMobileUrl}
                          variant={item.buttonVariant || "primary"}
                          size={item.buttonSize || "small"}
                          onClick={item.onClick}
                        >
                          {item.text}
                        </Button>
                      );
                    }

                    return (
                      <Link
                        key={index}
                        href={item.href}
                        noFollow={item.noFollow}
                        target={item.target}
                        onClick={item.onClick}
                        className="uipm:text-(length:--typography-sizes-body-medium) uipm:font-medium uipm:text-[var(--typography-colors-text-accent)] uipm:leading-[var(--typography-line-heights-body-medium)] hover:uipm:text-[var(--typography-colors-text-accent)] uipm:transition-colors uipm:cursor-pointer"
                      >
                        {item.text}
                      </Link>
                    );
                  })}
                </div>
              ) : signInHref ? (
                <Link
                  href={signInHref}
                  noFollow={signInNoFollow}
                  className="uipm:text-(length:--typography-sizes-body-medium) uipm:font-medium uipm:text-[var(--typography-colors-text-accent)] uipm:leading-[var(--typography-line-heights-body-medium)] uipm:hover:text-[var(--typography-colors-text-accent)] uipm:transition-colors uipm:cursor-pointer"
                >
                  {signInText}
                </Link>
              ) : (
                <button
                  onClick={onSignInClick}
                  className="uipm:text-(length:--typography-sizes-body-medium) uipm:font-medium uipm:text-[var(--typography-colors-text-accent)] uipm:leading-[var(--typography-line-heights-body-medium)] uipm:hover:text-[var(--typography-colors-text-accent)] uipm:transition-colors uipm:cursor-pointer"
                >
                  {signInText}
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div
        className="uipm:hidden uipm:lg:block uipm:fixed uipm:left-0 uipm:right-0 uipm:h-[1px] uipm:bg-[var(--color-color-light)] uipm:z-[50] uipm:pointer-events-none"
        style={{
          top: "calc(var(--notification-banner-height, 0px) + 90px)",
        }}
        aria-hidden="true"
      />

      {/* Spacer to prevent content from shifting under fixed navbar and banner */}
      <div
        className="uipm:hidden uipm:lg:block"
        style={{
          height: "calc(var(--notification-banner-height, 0px) + 90px)",
        }}
        aria-hidden="true"
      />
    </>
  );
}
